import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Avatar, Textarea, Modal, ModalDialog, ModalClose,
    Checkbox,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon       from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon     from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon          from '@mui/icons-material/CancelRounded';
import RuleRoundedIcon            from '@mui/icons-material/RuleRounded';
import AccessTimeRoundedIcon      from '@mui/icons-material/AccessTimeRounded';
import CalendarTodayRoundedIcon   from '@mui/icons-material/CalendarTodayRounded';
import EditNoteRoundedIcon        from '@mui/icons-material/EditNoteRounded';
import AddCircleRoundedIcon       from '@mui/icons-material/AddCircleRounded';
import RemoveCircleRoundedIcon    from '@mui/icons-material/RemoveCircleRounded';
import SwapHorizRoundedIcon       from '@mui/icons-material/SwapHorizRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetPendingChangeRequests,
    APIReviewChangeRequest,
    APIReviewChangeRequestsBatch,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const DAYS_FULL = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];

const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return `${DAYS_FULL[d.getDay()]}, ${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;
};

const fmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getDate()} ${MONTHS_RU[d.getMonth()]} в ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
};

const getInitials = (name = '') => {
    const p = name.trim().split(' ');
    return (p[0]?.[0] || '') + (p[1]?.[0] || '');
};

const CHANGE_TYPE_CONFIG = {
    add:    { label: 'Добавить день',    icon: <AddCircleRoundedIcon sx={{ fontSize: 15 }} />,    color: '#22c55e',  bg: 'rgba(34,197,94,0.1)'   },
    remove: { label: 'Убрать день',      icon: <RemoveCircleRoundedIcon sx={{ fontSize: 15 }} />, color: '#f87171',  bg: 'rgba(248,113,113,0.1)' },
    swap:   { label: 'Заменить день',    icon: <SwapHorizRoundedIcon sx={{ fontSize: 15 }} />,    color: '#06b6d4',  bg: 'rgba(6,182,212,0.1)'   },
    edit:   { label: 'Изменить время',   icon: <AccessTimeRoundedIcon sx={{ fontSize: 15 }} />,   color: '#f59e0b',  bg: 'rgba(245,158,11,0.1)'  },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.07 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

// ─── Компонент: карточка запроса ──────────────────────────────────────────────
function RequestCard({ request, onAction, canReview, selected, onToggleSelect }) {
    const cfg = CHANGE_TYPE_CONFIG[request.change_type] || CHANGE_TYPE_CONFIG.edit;

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                bgcolor:      'var(--joy-palette-background-level1)',
                border:       '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                overflow:     'hidden',
                transition:   'border-color .2s',
                '&:hover':    { borderColor: 'rgba(162,38,226,0.3)' },
            }}>
                {/* Шапка карточки */}
                <Box sx={{
                    display:      'flex',
                    alignItems:   'center',
                    gap:          1.5,
                    px:           2.5,
                    py:           1.8,
                    borderBottom: '1px solid var(--joy-palette-divider)',
                    bgcolor:      'var(--joy-palette-background-level2)',
                }}>
                    {canReview && onToggleSelect && (
                        <Checkbox
                            checked={!!selected}
                            onChange={() => onToggleSelect(request.id)}
                            sx={{ flexShrink: 0 }}
                        />
                    )}

                    <Avatar src={request.account_avatar_url} size="sm" sx={{
                        width: 34, height: 34, fontSize: 12, fontWeight: 700, flexShrink: 0,
                        bgcolor: 'rgba(162,38,226,0.18)',
                        color:   'var(--joy-palette-primary-300)',
                        border:  '1.5px solid rgba(162,38,226,0.3)',
                    }}>
                        {(request.account_name || '').charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                            {request.account_name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                            {fmtDateTime(request.date_create)}
                        </Typography>
                    </Box>

                    <Chip
                        size="sm"
                        startDecorator={cfg.icon}
                        sx={{
                            bgcolor: cfg.bg, color: cfg.color,
                            fontWeight: 700, fontSize: 11, border: 'none',
                        }}
                    >
                        {cfg.label}
                    </Chip>
                </Box>

                {/* Тело карточки */}
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: request.comment ? 1.5 : 0 }}>
                        {/* Дата (для swap — переход откуда → куда) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                            {request.change_type === 'swap' ? (
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                                    <Box component="span" sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                        {fmtDate(request.date)}
                                    </Box>
                                    <SwapHorizRoundedIcon sx={{ fontSize: 15, color: '#06b6d4' }} />
                                    <Box component="span" sx={{ color: '#06b6d4', fontWeight: 700 }}>
                                        {fmtDate(request.swap_to_date)}
                                    </Box>
                                </Typography>
                            ) : (
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)', fontWeight: 600 }}>
                                    {fmtDate(request.date)}
                                </Typography>
                            )}
                        </Box>

                        {/* Время */}
                        {request.change_type === 'edit' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                    {request.old_time ? request.old_time.slice(0, 5) : '—'}
                                    {' → '}
                                    <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                        {request.new_time ? request.new_time.slice(0, 5) : '—'}
                                    </Box>
                                </Typography>
                            </Box>
                        )}
                        {(request.change_type === 'add' || request.change_type === 'swap') && request.new_time && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <AccessTimeRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                    С <Box component="span" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                        {request.new_time.slice(0, 5)}
                                    </Box>
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Комментарий */}
                    {request.comment && (
                        <Box sx={{
                            mt: 1.5, px: 1.5, py: 1.2,
                            bgcolor: 'var(--joy-palette-background-level2)',
                            borderRadius: 'var(--joy-radius-md)',
                            border: '1px solid var(--joy-palette-divider)',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                                <EditNoteRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)', mt: 0.2, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', lineHeight: 1.5 }}>
                                    {request.comment}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Кнопки */}
                    {canReview && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button
                                size="sm"
                                startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}
                                onClick={() => onAction(request, 'approve')}
                                sx={{
                                    background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 12,
                                    border: 'none',
                                    boxShadow: '0 3px 10px rgba(34,197,94,0.3)',
                                    '&:hover': { boxShadow: '0 5px 16px rgba(34,197,94,0.45)' },
                                }}
                            >
                                Одобрить
                            </Button>
                            <Button
                                size="sm"
                                variant="outlined"
                                startDecorator={<CancelRoundedIcon sx={{ fontSize: 15 }} />}
                                onClick={() => onAction(request, 'reject')}
                                sx={{
                                    borderColor: 'rgba(248,113,113,0.4)',
                                    color:       '#f87171',
                                    fontWeight:  700, fontSize: 12,
                                    '&:hover':   { bgcolor: 'rgba(248,113,113,0.08)', borderColor: '#f87171' },
                                }}
                            >
                                Отклонить
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}

// ─── Модалка с ответом ────────────────────────────────────────────────────────
function ReviewModal({ open, request, action, onClose, onSubmit, loading }) {
    const [comment, setComment] = useState('');

    useEffect(() => {
        if (open) setComment('');
    }, [open]);

    const isApprove = action === 'approve';
    const cfg       = request ? CHANGE_TYPE_CONFIG[request.change_type] : null;

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border:  '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                    maxWidth: 440, width: '100%',
                    p: 3,
                }}
            >
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                {/* Заголовок */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{
                        fontSize: 18, fontWeight: 800,
                        color: isApprove ? '#22c55e' : '#f87171',
                        letterSpacing: '-0.01em',
                    }}>
                        {isApprove ? '✓ Одобрить запрос' : '✕ Отклонить запрос'}
                    </Typography>
                    {request && (
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                            {request.account_name} · {cfg?.label} ·{' '}
                            {request.change_type === 'swap'
                                ? `${fmtDate(request.date)} → ${fmtDate(request.swap_to_date)}`
                                : fmtDate(request.date)}
                        </Typography>
                    )}
                </Box>

                {/* Поле ответа */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.8 }}>
                        Комментарий {!isApprove && '(необязательно)'}
                    </Typography>
                    <Textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder={isApprove ? 'Пояснение для фотографа…' : 'Причина отказа…'}
                        minRows={2}
                        maxRows={4}
                        sx={{
                            bgcolor: 'var(--joy-palette-background-level2)',
                            border:  '1px solid var(--joy-palette-divider)',
                            color: 'white',
                            fontSize: 13,
                            '&:focus-within': { borderColor: isApprove ? '#22c55e' : '#f87171' },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={() => onSubmit(comment)}
                        loading={loading}
                        sx={{
                            flex: 1,
                            background: isApprove
                                ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                                : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                            color: '#fff', fontWeight: 700,
                            border: 'none',
                            boxShadow: isApprove
                                ? '0 4px 16px rgba(34,197,94,0.35)'
                                : '0 4px 16px rgba(248,113,113,0.35)',
                        }}
                    >
                        {isApprove ? 'Одобрить' : 'Отклонить'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            borderColor: 'var(--joy-palette-divider)',
                            color: 'var(--joy-palette-text-secondary)',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                        }}
                    >
                        Отмена
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  ChangeRequestsPage
// ════════════════════════════════════════════════════════════════════
export default function ChangeRequestsPage() {
    const { token, showToast, hasPerm, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [selectedIds,  setSelectedIds]  = useState([]);   // выбранные заявки
    const [batchLoading, setBatchLoading] = useState(false);

    const canReview = hasPerm('schedule.change_requests.review');

    // Модалка
    const [modalOpen,    setModalOpen]    = useState(false);
    const [modalRequest, setModalRequest] = useState(null);
    const [modalAction,  setModalAction]  = useState('approve');
    const [submitting,   setSubmitting]   = useState(false);

    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            const data = await APIGetPendingChangeRequests(token);
            setLoading(false);
            if (Array.isArray(data)) { setRequests(data); return true; }
            return false;
        } catch (e) { setLoading(false); return false; }
    }, [token]);

    useEffect(() => { loadRequests(); }, [loadRequests]);
    useEffect(() => onRefreshTick(loadRequests), [onRefreshTick, loadRequests]);

    const handleAction = (request, action) => {
        setModalRequest(request);
        setModalAction(action);
        setModalOpen(true);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleBatch = async (action) => {
        if (selectedIds.length === 0) return;
        setBatchLoading(true);
        const res = await APIReviewChangeRequestsBatch(token, selectedIds, action);
        setBatchLoading(false);
        if (!res?.error) {
            const label = action === 'approve' ? 'одобрено' : 'отклонено';
            showToast(`Массово ${label}: ${res.count ?? selectedIds.length} ✓`, action === 'approve' ? 'success' : 'warning');
            setRequests(prev => prev.filter(r => !selectedIds.includes(r.id)));
            setSelectedIds([]);
        } else {
            showToast('Ошибка массового рассмотрения', 'danger');
        }
    };

    const handleSubmit = async (comment) => {
        setSubmitting(true);
        const res = await APIReviewChangeRequest(token, modalRequest.id, {
            action:  modalAction,
            comment: comment || '',
        });
        setSubmitting(false);

        if (!res?.error) {
            const label = modalAction === 'approve' ? 'одобрен' : 'отклонён';
            showToast(`Запрос ${label} ✓`, modalAction === 'approve' ? 'success' : 'warning');
            setModalOpen(false);
            setRequests(prev => prev.filter(r => r.id !== modalRequest.id));
        } else {
            showToast('Ошибка при обработке запроса', 'danger');
        }
    };

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <Box
                            onClick={() => navigate('/manager')}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 36, height: 36,
                                borderRadius: 'var(--joy-radius-md)',
                                border: '1px solid var(--joy-palette-divider)',
                                bgcolor: 'var(--joy-palette-background-level1)',
                                cursor: 'pointer', transition: 'all .15s',
                                '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                            }}
                        >
                            <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Запросы на изменение
                                </Typography>
                                {requests.length > 0 && (
                                    <Chip size="sm" sx={{
                                        bgcolor: 'rgba(245,158,11,0.15)',
                                        color: '#f59e0b',
                                        fontWeight: 800, fontSize: 12,
                                        border: '1px solid rgba(245,158,11,0.3)',
                                    }}>
                                        {requests.length}
                                    </Chip>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                Ожидают вашего решения
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : requests.length === 0 ? (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}>
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            py: 8, gap: 2,
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                        }}>
                            <Box sx={{
                                width: 56, height: 56, borderRadius: '50%',
                                bgcolor: 'rgba(34,197,94,0.12)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 28, color: '#22c55e' }} />
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                    Всё обработано
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', mt: 0.4 }}>
                                    Новых запросов нет
                                </Typography>
                            </Box>
                        </Box>
                    </motion.div>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        {canReview && selectedIds.length > 0 && (
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                                p: 1, borderRadius: 'var(--joy-radius-lg)',
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                            }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
                                    Выбрано: {selectedIds.length}
                                </Typography>
                                <Button size="sm" color="success" variant="soft" loading={batchLoading}
                                    onClick={() => handleBatch('approve')}>
                                    Одобрить
                                </Button>
                                <Button size="sm" color="danger" variant="soft" loading={batchLoading}
                                    onClick={() => handleBatch('reject')}>
                                    Отклонить
                                </Button>
                                <Button size="sm" variant="plain" color="neutral"
                                    onClick={() => setSelectedIds([])}>
                                    Сброс
                                </Button>
                            </Box>
                        )}
                        <Stack spacing={1.5}>
                            {requests.map(req => (
                                <RequestCard key={req.id} request={req} onAction={handleAction} canReview={canReview}
                                    selected={selectedIds.includes(req.id)}
                                    onToggleSelect={toggleSelect} />
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            <ReviewModal
                open={modalOpen}
                request={modalRequest}
                action={modalAction}
                loading={submitting}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />
        </motion.div>
    );
}